import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldHelp } from "@/components/ui/field-help";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { deskDocHelp } from "@/modules/core/help/desk-doc-help";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { AppPicker } from "@/modules/shell/AppPicker";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { listProducts, type ProductRow } from "@/shared/api/inventory";
import {
  createPurchaseInvoice,
  getPurchaseOrderInvoiceOpen,
  listSuppliers,
  type PurchaseOrderInvoiceOpenLine,
} from "@/shared/api/purchasing";
import { notify } from "@/shared/feedback/notify";

type LineDraft = { productId: string; qty: string; unitCost: string };
type SupplierRow = { id: string; name: string };

/** From-order or standalone PI — plan/41 Model C. */
export function PurchaseInvoiceNewPage() {
  const { companySlug } = useParams();
  const [searchParams] = useSearchParams();
  const purchaseOrderId = searchParams.get("purchaseOrderId") ?? "";

  if (!companySlug) return null;

  if (!purchaseOrderId) {
    return <StandalonePurchaseInvoiceForm companySlug={companySlug} />;
  }

  return (
    <FromOrderPurchaseInvoiceForm
      companySlug={companySlug}
      purchaseOrderId={purchaseOrderId}
    />
  );
}

function StandalonePurchaseInvoiceForm(props: { companySlug: string }) {
  const { companySlug } = props;
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [supplierId, setSupplierId] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([
    { productId: "", qty: "1", unitCost: "" },
  ]);
  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState("");
  const [notes, setNotes] = useState("");
  const [submitOnCreate, setSubmitOnCreate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void Promise.all([listSuppliers(companySlug), listProducts(companySlug)])
      .then(([s, p]) => {
        if (cancelled) return;
        setSuppliers(s.suppliers);
        setProducts(p.products);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, navigate]);

  const billable = useMemo(
    () =>
      lines.filter(
        (l) =>
          l.productId &&
          Number(l.qty) > 0 &&
          l.unitCost.trim() !== "" &&
          Number(l.unitCost) >= 0,
      ),
    [lines],
  );

  async function onCreate() {
    if (!supplierId) {
      setError("Choose a supplier.");
      return;
    }
    if (billable.length === 0) {
      setError("Add at least one line with product, qty, and cost.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { invoice } = await createPurchaseInvoice(companySlug, {
        supplierId,
        submit: submitOnCreate,
        notes: notes.trim() || undefined,
        supplierInvoiceRef: supplierInvoiceRef.trim() || undefined,
        lines: billable.map((l) => ({
          productId: l.productId,
          qty: l.qty,
          unitCost: l.unitCost,
        })),
      });
      notify.success(
        submitOnCreate
          ? `Invoice ${invoice.number} submitted`
          : `Draft ${invoice.number} saved`,
      );
      navigate(`/c/${companySlug}/purchasing/invoices/${invoice.id}`, {
        replace: true,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageFrame width="narrow">
      <div className="mb-3">
        <DeskDocBackLink
          listBasePath={`/c/${companySlug}/purchasing/invoices`}
          listLabel="Purchase invoices"
        />
      </div>
      <PageHeader
        eyebrow="Purchase invoice"
        title="New invoice"
        description="Standalone supplier bill — no purchase order. Or create from an open order."
      />

      {loading ? (
        <Alert variant="muted" className="mt-4">
          Loading…
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      ) : null}

      {!loading ? (
        <div className="mt-6 space-y-4">
          <div>
            <Label htmlFor="pi-supplier">Supplier</Label>
            <AppPicker
              id="pi-supplier"
              className="mt-1"
              title="Supplier"
              description="Search suppliers"
              placeholder="Choose supplier"
              value={supplierId || null}
              options={suppliers.map((s) => ({
                value: s.id,
                label: s.name,
                keywords: s.name,
              }))}
              onChange={(id) => setSupplierId(id ?? "")}
            />
          </div>

          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
              Lines
            </div>
            {lines.map((line, i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <AppPicker
                  className="col-span-3"
                  title="Product"
                  description="Search products"
                  placeholder="Product…"
                  value={line.productId || null}
                  options={products.map((p) => ({
                    value: p.id,
                    label: p.name,
                    keywords: `${p.name} ${p.sku ?? ""} ${p.barcode ?? ""}`,
                    description: p.sku ?? undefined,
                  }))}
                  onChange={(id) => {
                    const next = [...lines];
                    next[i] = { ...line, productId: id ?? "" };
                    setLines(next);
                  }}
                />
                <Input
                  className="font-mono"
                  placeholder="Qty"
                  value={line.qty}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, qty: e.target.value };
                    setLines(next);
                  }}
                />
                <Input
                  className="col-span-2 font-mono text-right tabular-nums"
                  inputMode="decimal"
                  placeholder="Unit cost"
                  value={line.unitCost}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, unitCost: e.target.value };
                    setLines(next);
                  }}
                />
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setLines((prev) => [
                  ...prev,
                  { productId: "", qty: "1", unitCost: "" },
                ])
              }
            >
              Add line
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pi-supplier-inv-standalone">Supplier invoice #</Label>
            <Input
              id="pi-supplier-inv-standalone"
              value={supplierInvoiceRef}
              onChange={(e) => setSupplierInvoiceRef(e.target.value)}
              placeholder="Optional external bill #"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pi-notes-standalone">Notes</Label>
            <Input
              id="pi-notes-standalone"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={submitOnCreate}
              onChange={(e) => setSubmitOnCreate(e.target.checked)}
            />
            Submit immediately (open / posted)
          </label>

          <p className="m-0 text-xs text-[var(--color-muted)]">
            Prefer billing a PO? Open the order and choose Invoice, or{" "}
            <Link
              to={`/c/${companySlug}/purchasing/orders`}
              className="underline-offset-2 hover:underline"
            >
              Orders
            </Link>
            .
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={busy || billable.length === 0 || !supplierId}
              pending={busy}
              pendingLabel="Creating…"
              onClick={() => void onCreate()}
            >
              {submitOnCreate ? "Create & submit" : "Save draft"}
            </Button>
            <Button asChild type="button" variant="outline" size="sm">
              <Link
                to={`/c/${companySlug}/purchasing/invoices`}
                className="no-underline"
              >
                Cancel
              </Link>
            </Button>
          </div>
        </div>
      ) : null}
    </PageFrame>
  );
}

function FromOrderPurchaseInvoiceForm(props: {
  companySlug: string;
  purchaseOrderId: string;
}) {
  const { companySlug, purchaseOrderId } = props;
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState("");
  const [lines, setLines] = useState<PurchaseOrderInvoiceOpenLine[]>([]);
  const [qtyByLine, setQtyByLine] = useState<Record<string, string>>({});
  const [billOrdered, setBillOrdered] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitOnCreate, setSubmitOnCreate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  function qtyCap(l: PurchaseOrderInvoiceOpenLine, ordered: boolean) {
    return ordered ? l.qtyRemainingOrdered : l.qtyRemaining;
  }

  function fillQtys(
    nextLines: PurchaseOrderInvoiceOpenLine[],
    ordered: boolean,
  ) {
    const init: Record<string, string> = {};
    for (const l of nextLines) {
      const cap = qtyCap(l, ordered);
      init[l.purchaseOrderLineId] = Number(cap) > 0 ? cap : "0";
    }
    setQtyByLine(init);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPurchaseOrderInvoiceOpen(companySlug, purchaseOrderId)
      .then((d) => {
        if (cancelled) return;
        setOrderNumber(d.order.number);
        setSupplierInvoiceRef(d.order.supplierInvoiceRef ?? "");
        setLines(d.lines);
        setBillOrdered(false);
        fillQtys(d.lines, false);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, purchaseOrderId, navigate]);

  const billable = useMemo(
    () =>
      lines.filter((l) => {
        const q = Number(qtyByLine[l.purchaseOrderLineId] ?? 0);
        return q > 0;
      }),
    [lines, qtyByLine],
  );

  async function onCreate() {
    if (billable.length === 0) {
      setError("Enter a positive qty on at least one line.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { invoice } = await createPurchaseInvoice(companySlug, {
        purchaseOrderId,
        submit: submitOnCreate,
        notes: notes.trim() || undefined,
        supplierInvoiceRef: supplierInvoiceRef.trim() || undefined,
        lines: billable.map((l) => ({
          productId: l.productId,
          qty: qtyByLine[l.purchaseOrderLineId]!,
          purchaseOrderLineId: l.purchaseOrderLineId,
        })),
      });
      notify.success(
        submitOnCreate
          ? `Invoice ${invoice.number} submitted`
          : `Draft ${invoice.number} saved`,
      );
      navigate(`/c/${companySlug}/purchasing/invoices/${invoice.id}`, {
        replace: true,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageFrame width="narrow">
      <div className="mb-3">
        <Link
          to={`/c/${companySlug}/purchasing/orders/${purchaseOrderId}`}
          className="text-sm text-[var(--color-muted)] no-underline hover:text-[var(--color-foreground)] hover:underline"
        >
          ← {orderNumber ? `Order ${orderNumber}` : "Purchase order"}
        </Link>
      </div>
      <PageHeader
        eyebrow="Purchase invoice"
        title={orderNumber ? `Invoice ${orderNumber}` : "New invoice"}
        description="Prefills received open qty. Leave a line at 0 to skip it."
      />

      {loading && (
        <Alert variant="muted" className="mt-4">
          Loading open-to-invoice…
        </Alert>
      )}
      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      {!loading && lines.length === 0 && !error ? (
        <Alert variant="muted" className="mt-4">
          This order has no lines to invoice.
        </Alert>
      ) : null}

      {!loading && lines.length > 0 ? (
        <div className="mt-6 space-y-4">
          <p className="m-0 flex flex-wrap items-center gap-1.5 text-sm text-[var(--color-muted)]">
            Prefills received open qty.
            <FieldHelp content={deskDocHelp.billFromOrder} />
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-muted)]">
                  <th className="py-2 pr-3 font-medium">Product</th>
                  <th className="py-2 pr-3 font-medium text-right">Ordered</th>
                  <th className="py-2 pr-3 font-medium text-right">Received</th>
                  <th className="py-2 pr-3 font-medium text-right">Invoiced</th>
                  <th className="py-2 pr-3 font-medium text-right">
                    {billOrdered ? "Ordered left" : "Received left"}
                  </th>
                  <th className="py-2 font-medium text-right">Bill qty</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const cap = qtyCap(l, billOrdered);
                  return (
                    <tr
                      key={l.purchaseOrderLineId}
                      className="border-b border-[var(--color-border)]/60"
                    >
                      <td className="py-2 pr-3">
                        {l.productName}
                        <div className="font-mono text-xs text-[var(--color-muted)]">
                          {moneyDisplay(l.unitCost)}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">
                        {l.qtyOrdered}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">
                        {l.qtyReceived}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">
                        {l.qtyInvoiced}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">
                        {cap}
                      </td>
                      <td className="py-2 text-right">
                        <Input
                          className="ml-auto w-24 text-right font-mono"
                          inputMode="decimal"
                          value={qtyByLine[l.purchaseOrderLineId] ?? ""}
                          disabled={Number(cap) <= 0}
                          onChange={(e) =>
                            setQtyByLine((prev) => ({
                              ...prev,
                              [l.purchaseOrderLineId]: e.target.value,
                            }))
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!billOrdered &&
          billable.length === 0 &&
          lines.some((l) => Number(l.qtyRemainingOrdered) > 0) ? (
            <Alert variant="muted">
              Nothing received left to bill. Open Advanced to bill ordered
              remainder.
            </Alert>
          ) : null}

          <AdvancedSection subtitle="Bill ordered">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={billOrdered}
                onChange={(e) => {
                  const next = e.target.checked;
                  setBillOrdered(next);
                  fillQtys(lines, next);
                }}
              />
              <span>
                <span className="font-medium">Bill ordered remainder</span>
                <span className="mt-0.5 block text-[var(--color-muted)]">
                  Prefill ordered − invoiced (still capped at ordered). Use for
                  deposits before full receive.
                </span>
              </span>
            </label>
          </AdvancedSection>

          <div className="space-y-2">
            <Label htmlFor="pi-supplier-inv">Supplier invoice #</Label>
            <Input
              id="pi-supplier-inv"
              value={supplierInvoiceRef}
              onChange={(e) => setSupplierInvoiceRef(e.target.value)}
              placeholder="From goods receipt when present"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pi-notes">Notes</Label>
            <Input
              id="pi-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={submitOnCreate}
              onChange={(e) => setSubmitOnCreate(e.target.checked)}
            />
            Submit immediately (open / posted)
          </label>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={busy || billable.length === 0}
              pending={busy}
              pendingLabel="Creating…"
              onClick={() => void onCreate()}
            >
              {submitOnCreate ? "Create & submit" : "Save draft"}
            </Button>
            <Button asChild type="button" variant="outline" size="sm">
              <Link
                to={`/c/${companySlug}/purchasing/orders/${purchaseOrderId}`}
                className="no-underline"
              >
                Cancel
              </Link>
            </Button>
          </div>
        </div>
      ) : null}
    </PageFrame>
  );
}
